import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Footprints } from 'lucide-react';

const PILLARS = [
  {
    title: 'Soutien ergonomique',
    text: 'Une voûte plantaire parfaitement soutenue pour un alignement naturel du pied et une posture saine.',
  },
  {
    title: 'Confort absolu',
    text: 'Des semelles mémoire qui amortissent chaque pas et soulagent les points de pression.',
  },
  {
    title: 'Bien-être au quotidien',
    text: 'Soulage les douleurs et la fatigue, pour des journées légères et des pieds sereins.',
  },
];

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const ZenPhilosophy = ({ image }) => {
  return (
    <section className="relative overflow-hidden bg-white py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        {/* Photo */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="relative mx-auto w-full max-w-lg lg:max-w-none"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[1.75rem] bg-[#efe4d6]">
            {image ? (
              <img
                src={image}
                alt="Chaussures orthopédiques Tendresse"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1500ms] hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#174540] to-[#0c2a27]">
                <span className="font-serif text-6xl italic text-white">Tendresse</span>
              </div>
            )}
          </div>

          <div className="absolute -bottom-8 right-4 max-w-[16rem] rounded-2xl bg-[#174540] p-6 text-white shadow-[0_30px_60px_-30px_rgba(12,42,39,0.8)] sm:-right-6">
            <Footprints className="h-6 w-6 text-[#c9a078]" strokeWidth={1.5} />
            <p className="mt-3 font-serif text-2xl leading-tight">Le soin du pied, avant tout.</p>
            <p className="mt-2 text-xs leading-relaxed text-white/70">
              Chaque modèle est choisi pour son confort et son maintien.
            </p>
          </div>
        </motion.div>

        {/* Copy */}
        <div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }}>
            <span className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#3d8361]">
              <span className="h-px w-8 bg-[#3d8361]/50" />
              Pourquoi Tendresse
            </span>
            <h2 className="mb-0 mt-6 text-left font-serif text-4xl font-medium leading-[1.05] text-[#0c2a27] sm:text-5xl">
              Pensées pour soulager,
              <span className="block italic text-[#174540]">dessinées pour plaire.</span>
            </h2>
            <p className="mt-6 max-w-lg font-light leading-relaxed text-[#3a4a47]">
              Chez Tendresse, chaque paire est pensée pour la femme active. Nous allions expertise
              orthopédique, confort absolu et élégance pour accompagner chacun de vos pas, en toute sérénité.
            </p>
          </motion.div>

          <motion.ol
            variants={listVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-10 divide-y divide-[#0c2a27]/10 border-y border-[#0c2a27]/10"
          >
            {PILLARS.map((pillar, i) => (
              <motion.li key={pillar.title} variants={reveal} className="flex gap-6 py-6">
                <span className="font-serif text-2xl italic text-[#c9a078]">{String(i + 1).padStart(2, '0')}</span>
                <span>
                  <span className="block text-base font-semibold text-[#0c2a27]">{pillar.title}</span>
                  <span className="mt-1 block text-sm font-light leading-relaxed text-[#3a4a47]">{pillar.text}</span>
                </span>
              </motion.li>
            ))}
          </motion.ol>

          <a
            href="#categories"
            className="group mt-10 inline-flex items-center gap-3 text-sm font-semibold text-[#174540]"
          >
            <span className="border-b border-[#174540]/30 pb-1 transition-colors group-hover:border-[#174540]">
              Explorer les collections
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ZenPhilosophy;
